import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import GroupIcon from '@mui/icons-material/GroupOutlined'
import StorefrontIcon from '@mui/icons-material/Storefront'
import AddIcon from '@mui/icons-material/Add'
import { useSessionStore } from '../stores/sessionStore.js'
import SettingsIcon from '@mui/icons-material/SettingsOutlined'
import { IconButton, Tooltip } from '@mui/material'
import { NouvelleBoutiqueDialog } from '../features/stores/NouvelleBoutiqueDialog.js'
import { ReglagesBoutiqueDialog } from '../features/stores/ReglagesBoutiqueDialog.js'
import { SitesVitrines } from '../features/site/SitesVitrines.js'
import { fondPastille } from '../ui/pastille.js'

const ROLE_LABELS: Record<string, string> = {
  store_admin: 'Administrateur',
  employee: 'Employé',
  viewer: 'Lecture seule',
  platform_admin: 'Administrateur plateforme',
}

/** Sélecteur de boutique (W-03). */
export function StorePickerPage(): JSX.Element {
  const navigate = useNavigate()
  const { user, stores, logout, rememberStore, restore } = useSessionStore()
  const [filter, setFilter] = useState('')
  const [creation, setCreation] = useState(false)
  const [reglages, setReglages] = useState<string | null>(null)
  /**
   * ⚠️ L'onglet des sites n'existe QUE pour l'exploitant : la création et la
   * mise en ligne d'un site public sont ses gestes, pas ceux d'un gérant.
   * L'afficher à tous puis refuser au clic apprendrait surtout à l'utilisateur
   * que l'application lui ment.
   */
  const [onglet, setOnglet] = useState(0)

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return stores
    return stores.filter(
      (s) => s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    )
  }, [stores, filter])

  function open(slug: string): void {
    rememberStore(slug)
    navigate(`/s/${slug}/dashboard`)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 4, md: 8 } }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}
        >
          <Box>
            <Typography variant="h1">Choisissez une boutique</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Connecté en tant que {user?.email}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* La console plateforme n'apparaît QUE pour qui en a les droits :
                une entrée visible mais refusée au clic apprend surtout à
                l'utilisateur que l'application lui ment. */}
            {user?.isPlatformAdmin && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreation(true)}
                >
                  Nouvelle boutique
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GroupIcon />}
                  onClick={() => navigate('/admin/utilisateurs')}
                >
                  Comptes
                </Button>
              </>
            )}
            <Button variant="outlined" onClick={() => void logout()}>
              Se déconnecter
            </Button>
          </Box>
        </Box>

        {user?.isPlatformAdmin && (
          <Tabs value={onglet} onChange={(_, v: number) => setOnglet(v)} sx={{ mb: 5 }}>
            <Tab label="Mes boutiques" />
            <Tab label="Site vitrine boutique" />
          </Tabs>
        )}

        {onglet === 1 && user?.isPlatformAdmin ? (
          <SitesVitrines />
        ) : stores.length === 0 ? (
          // État vide : un compte sans appartenance n'est pas une erreur, c'est
          // un compte qui attend d'être rattaché.
          <Paper sx={{ p: 10, borderRadius: 3, textAlign: 'center' }}>
            <StorefrontIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 3 }} />
            <Typography variant="h3" sx={{ mb: 2 }}>
              Aucune boutique
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Votre compte n’est rattaché à aucune boutique.
              {user?.isPlatformAdmin
                ? ' Créez-en une depuis la console d’administration.'
                : ' Demandez à un administrateur de vous y ajouter.'}
            </Typography>
          </Paper>
        ) : (
          <>
            {stores.length > 3 && (
              <TextField
                placeholder="Filtrer…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
                sx={{ mb: 5, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <Box
              sx={{
                display: 'grid',
                gap: 4,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
              }}
            >
              {visible.map((store) => (
                <Paper
                  key={store.storeId}
                  onClick={() => open(store.slug)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      open(store.slug)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{
                    p: 6,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'border-color .15s, transform .15s',
                    '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
                    <Avatar
                      src={store.logoUrl ?? undefined}
                      sx={{ width: 44, height: 44, ...fondPastille(store.logoUrl) }}
                    >
                      {store.name.slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }} />
                    {user?.isPlatformAdmin && (
                      <Tooltip title="Réglages de la boutique">
                        <IconButton
                          size="small"
                          aria-label={`Réglages de ${store.name}`}
                          onClick={(e) => {
                            // ⚠️ La carte entière ouvre la boutique : sans cet
                            // arrêt, régler la boutique y entrerait aussitôt.
                            e.stopPropagation()
                            setReglages(store.storeId)
                          }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {store.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    /s/{store.slug}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                    <Chip size="small" label={ROLE_LABELS[store.role] ?? store.role} />
                    <Chip size="small" variant="outlined" label={store.currencyCode} />
                  </Box>
                </Paper>
              ))}
            </Box>

            {visible.length === 0 && (
              <Typography sx={{ color: 'text.secondary', mt: 6 }}>
                Aucune boutique ne correspond à « {filter} ».
              </Typography>
            )}
          </>
        )}
      </Box>

      <ReglagesBoutiqueDialog
        storeId={reglages}
        onClose={() => setReglages(null)}
        onEnregistre={async () => {
          setReglages(null)
          await restore()
        }}
      />

      <NouvelleBoutiqueDialog
        open={creation}
        onClose={() => setCreation(false)}
        onCreee={async () => {
          setCreation(false)
          // ⚠️ La liste vient des APPARTENANCES portées par la session : sans
          // ce rappel, la boutique existe mais reste invisible jusqu'à la
          // prochaine connexion.
          await restore()
        }}
      />
    </Box>
  )
}
